/**
 * Организации в мессенджере (03.10.2026).
 * OrgsPanel — страница «Мои организации» в своём профиле (ProfileSelfModal): список моих организаций,
 *   поиск и создание; по нажатию — страница организации (OrgPage) со всеми её настройками:
 *   заявки, участники, администраторы, переименование, удаление / выход.
 *   Владелец назначает администраторов — они сами принимают и отклоняют заявки, убирают участников.
 * OrgsOnboarding — окно «Где вы работаете?» для только что зарегистрировавшегося гостя
 *   (роль external), пока у него нет ни одной организации или заявки.
 * Классы — внутри .self-profile-modal (кнопки/поля sp-* из chat-redesign.css), свои — styles/orgs.css.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Avatar } from "@/components/prototype/Avatar";
import { Icon } from "@/components/prototype/Icon";
import { useAuth } from "@/auth/AuthContext";
import { parseBackendIso } from "@/lib/presence";
import {
  ORGS_KEY, fetchMyOrgs, searchOrgs, createOrg, joinOrg, leaveOrg, fetchOrgMembers,
  approveOrgRequest, rejectOrgRequest, removeOrgMember, deleteOrg, renameOrg, makeOrgAdmin, unmakeOrgAdmin,
  fetchOrgCandidates, addOrgMember, disableOrgAccount, enableOrgAccount,
  type Org, type OrgCandidate, type OrgMember, type OrgMembers,
} from "@/api/orgs";
import "@/styles/orgs.css";


function plural(n: number, one: string, few: string, many: string): string {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return many;
  if (b > 1 && b < 5) return few;
  if (b === 1) return one;
  return many;
}
const membersText = (n: number) => `${n} ${plural(n, "участник", "участника", "участников")}`;
const errText = (e: unknown) => (e instanceof Error && e.message ? e.message : "Не получилось, попробуйте ещё раз");
const sameName = (a: string, b: string) => {
  const k = (s: string) => s.replace(/[«»"'„“”`]/g, "").replace(/\s+/g, " ").trim().toLowerCase().replace(/ё/g, "е");
  return k(a) === k(b);
};

function fmtDate(iso: string | null): string {
  const d = iso ? parseBackendIso(iso) : null;
  return d ? d.toLocaleDateString("ru-RU", { day: "numeric", month: "long" }) : "";
}

function statusText(o: Org): string {
  if (o.status === "pending") return `Ждёт подтверждения${o.owner_name ? ` · ${o.owner_name}` : ""}`;
  if (o.is_owner) return `Вы владелец · ${membersText(o.members)}`;
  if (o.is_admin) return `Вы администратор · ${membersText(o.members)}`;
  if (o.implicit) return `Вы сотрудник · ${membersText(o.members)}`;
  return `Подтверждено · ${membersText(o.members)}`;
}

/** Состав организаций поменялся — обновить свои списки. Списки людей сервер кэширует 30 с. */
function useInvalidateOrgs() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ORGS_KEY });
    void qc.invalidateQueries({ queryKey: ["orgs/members"] });
    void qc.invalidateQueries({ queryKey: ["orgs/search"] });
    void qc.invalidateQueries({ queryKey: ["fwd/contacts"] });
  };
}


export function OrgsPanel() {
  const myQ = useQuery({ queryKey: ORGS_KEY, queryFn: fetchMyOrgs, staleTime: 15_000 });
  const [openId, setOpenId] = useState<number | null>(null);
  const autoOpened = useRef(false);
  const orgs = myQ.data?.orgs ?? [];

  // Открыли сразу на этой вкладке (пуш → ?orgs=1): на телефоне она за краем ленты — показать.
  // Повтор после загрузки: цифра заявок на вкладке появляется позже и делает её шире.
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      document.querySelector(".self-profile-modal .self-profile-tab.active")
        ?.scrollIntoView({ block: "nearest", inline: "nearest" });
    });
    return () => cancelAnimationFrame(id);
  }, [myQ.data]);

  // Владельцу/администратору с новыми заявками — сразу страница этой организации (пуш «Заявка в организацию»).
  useEffect(() => {
    if (autoOpened.current || !myQ.data) return;
    autoOpened.current = true;
    const withRequests = myQ.data.orgs.find(o => o.pending > 0);
    if (withRequests) setOpenId(withRequests.id);
  }, [myQ.data]);

  const open = openId != null ? orgs.find(o => o.id === openId) : undefined;
  if (open) return <OrgPage org={open} onBack={() => setOpenId(null)}/>;

  return (
    <div className="org-panel">
      <div className="org-intro">
        В «Переслать», новом чате и участниках групп вы видите только людей из своих организаций,
        и они — вас. Принадлежность подтверждает владелец организации или назначенный им администратор.
      </div>

      <div>
        <div className="org-label">Мои организации</div>
        {myQ.isLoading ? (
          <div className="org-empty">Загрузка…</div>
        ) : orgs.length === 0 ? (
          <div className="org-empty">Пока ни одной. Найдите свою организацию ниже или создайте новую.</div>
        ) : (
          <div className="org-list">
            {orgs.map(o => (
              <button key={o.id} type="button" className="org-row org-row-link" onClick={() => setOpenId(o.id)}>
                <div className="org-mark"><Icon name="users" size={16}/></div>
                <div className="org-row-body">
                  <div className="org-row-name">{o.name}</div>
                  <div className={`org-row-sub${o.status === "pending" ? " is-pending" : ""}`}>{statusText(o)}</div>
                </div>
                {o.pending > 0 && <span className="org-badge" title="Новые заявки">{o.pending}</span>}
                <span className="org-chevron"><Icon name="chevron_r" size={16}/></span>
              </button>
            ))}
          </div>
        )}
      </div>

      <OrgFinder/>
    </div>
  );
}


/** Страница организации: все её настройки в одном месте. */
function OrgPage({ org, onBack }: { org: Org; onBack: () => void }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const inval = useInvalidateOrgs();
  const [err, setErr] = useState<string | null>(null);
  const [name, setName] = useState(org.name);
  const manage = org.is_owner || org.is_admin;
  const membersQ = useQuery({
    queryKey: ["orgs/members", org.id],
    queryFn: () => fetchOrgMembers(org.id),
    enabled: org.status === "member",
  });

  useEffect(() => { setName(org.name); }, [org.name]);
  useEffect(() => {
    document.querySelector(".self-profile-modal .self-profile-body")?.scrollTo({ top: 0 });
  }, [org.id]);

  const put = (d: OrgMembers) => { qc.setQueryData(["orgs/members", org.id], d); setErr(null); inval(); };
  const fail = (e: unknown) => setErr(errText(e));
  const approve = useMutation({ mutationFn: (uid: string) => approveOrgRequest(org.id, uid), onSuccess: put, onError: fail });
  const reject = useMutation({ mutationFn: (uid: string) => rejectOrgRequest(org.id, uid), onSuccess: put, onError: fail });
  const remove = useMutation({ mutationFn: (uid: string) => removeOrgMember(org.id, uid), onSuccess: put, onError: fail });
  const setAdmin = useMutation({
    mutationFn: ({ uid, on }: { uid: string; on: boolean }) => (on ? makeOrgAdmin(org.id, uid) : unmakeOrgAdmin(org.id, uid)),
    onSuccess: put,
    onError: fail,
  });
  const toggleAccount = useMutation({
    mutationFn: ({ uid, off }: { uid: string; off: boolean }) => (off ? disableOrgAccount(org.id, uid) : enableOrgAccount(org.id, uid)),
    onSuccess: put,
    onError: fail,
  });
  const rename = useMutation({
    mutationFn: (n: string) => renameOrg(org.id, n),
    onSuccess: () => { setErr(null); inval(); },
    onError: fail,
  });
  const leave = useMutation({
    mutationFn: () => leaveOrg(org.id),
    onSuccess: () => { inval(); onBack(); },
    onError: fail,
  });
  const del = useMutation({
    mutationFn: () => deleteOrg(org.id),
    onSuccess: () => { inval(); onBack(); },
    onError: fail,
  });
  const busy = approve.isPending || reject.isPending || remove.isPending || setAdmin.isPending || toggleAccount.isPending
    || rename.isPending || leave.isPending || del.isPending;
  const d = membersQ.data;

  const canRemove = (m: OrgMember) =>
    manage && !m.is_owner && m.user_id !== user?.id && (org.is_owner || !m.is_admin);

  return (
    <div className="org-panel">
      <div className="org-page-head">
        <button type="button" className="org-back" onClick={onBack}>
          <Icon name="chevron_l" size={16}/> Мои организации
        </button>
        <div className="org-page-title">{org.name}</div>
        <div className={`org-row-sub${org.status === "pending" ? " is-pending" : ""}`}>{statusText(org)}</div>
      </div>

      {org.status === "pending" && (
        <div className="org-intro">
          Заявка ждёт подтверждения{org.owner_name ? ` — ${org.owner_name}` : ""}. Пока её не подтвердят,
          люди из этой организации не видят вас в списках.
        </div>
      )}

      {membersQ.isError && <div className="sp-error">{errText(membersQ.error)}</div>}

      {d && manage && (
        <div>
          <div className="org-label">Заявки{d.pending.length ? ` · ${d.pending.length}` : ""}</div>
          {d.pending.length === 0 ? (
            <div className="org-empty">Новых заявок нет</div>
          ) : (
            <div className="org-list">
              {d.pending.map(m => (
                <div key={m.user_id} className="org-row">
                  <Avatar name={m.display_name || "?"} userId={m.user_id} avatarFilename={m.avatar_filename} size={32} color="#4fae4e"/>
                  <div className="org-row-body">
                    <div className="org-row-name">{m.display_name || "Без имени"}</div>
                    <div className="org-row-sub">{[m.email, fmtDate(m.requested_at)].filter(Boolean).join(" · ")}</div>
                  </div>
                  <div className="org-row-actions">
                    <button type="button" className="sp-btn-primary org-btn-sm" disabled={busy}
                      onClick={() => approve.mutate(m.user_id)}>
                      Подтвердить
                    </button>
                    <button type="button" className="sp-btn-ghost org-btn-sm" disabled={busy}
                      onClick={() => reject.mutate(m.user_id)}>
                      Отклонить
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {d && manage && <OrgAdder org={org} onAdded={put}/>}

      {org.status === "member" && (
        <div>
          <div className="org-label">Участники{d ? ` · ${d.members.length}` : ""}</div>
          {membersQ.isLoading ? (
            <div className="org-empty">Загрузка…</div>
          ) : d && (
            <div className="org-list org-members">
              {d.members.map(m => (
                <div key={m.user_id} className="org-row">
                  <Avatar name={m.display_name || "?"} userId={m.user_id} avatarFilename={m.avatar_filename} size={32} color="#4fae4e"/>
                  <div className="org-row-body">
                    <div className="org-row-name">{m.display_name || "Без имени"}</div>
                    {(m.is_owner || m.is_admin || m.disabled) && (
                      <div className={`org-row-sub${m.disabled ? " is-off" : ""}`}>
                        {m.disabled ? "учётка выключена" : m.is_owner ? "владелец" : "администратор"}
                      </div>
                    )}
                  </div>
                  {m.disabled ? (m.can_enable && (
                    <div className="org-row-actions">
                      <button type="button" className="sp-btn-ghost org-btn-sm" disabled={busy}
                        onClick={() => toggleAccount.mutate({ uid: m.user_id, off: false })}>
                        Включить
                      </button>
                    </div>
                  )) : ((org.is_owner && !m.is_owner) || canRemove(m) || m.can_disable) && (
                    <div className="org-row-actions">
                      {org.is_owner && !m.is_owner && (
                        <button type="button" className="sp-btn-ghost org-btn-sm" disabled={busy}
                          onClick={() => setAdmin.mutate({ uid: m.user_id, on: !m.is_admin })}>
                          {m.is_admin ? "Снять администратора" : "Сделать администратором"}
                        </button>
                      )}
                      {canRemove(m) && (
                        <button type="button" className="sp-btn-ghost org-btn-sm" disabled={busy}
                          onClick={() => {
                            const who = m.display_name || "участника";
                            if (window.confirm(`Убрать ${who} из «${org.name}»? Он перестанет видеть людей отсюда в списках мессенджера, а они — его.`)) {
                              remove.mutate(m.user_id);
                            }
                          }}>
                          Убрать
                        </button>
                      )}
                      {m.can_disable && (
                        <button type="button" className="sp-btn-ghost org-btn-sm org-danger" disabled={busy}
                          onClick={() => {
                            const who = m.display_name || "участника";
                            if (window.confirm(`Выключить учётку: ${who}? Человек сразу выйдет на всех устройствах и не сможет войти — ни в мессенджер, ни в CRM. Включить обратно можно здесь же.`)) {
                              toggleAccount.mutate({ uid: m.user_id, off: true });
                            }
                          }}>
                          Выключить
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {org.is_owner && (
            <div className="org-hint">
              Администраторы сами принимают и отклоняют заявки, убирают участников и выключают учётки. Назначаете их только вы.
            </div>
          )}
          {org.is_admin && (
            <div className="org-hint">
              Вы администратор: принимаете и отклоняете заявки, убираете участников, выключаете учётки. Администраторов назначает
              владелец{org.owner_name ? ` — ${org.owner_name}` : ""}.
            </div>
          )}
        </div>
      )}

      <div>
        <div className="org-label">Настройки</div>
        <div className="org-settings">
          {org.is_owner && (
            <div className="org-rename">
              <input className="sp-input" value={name} maxLength={120} aria-label="Название организации"
                onChange={(e) => setName(e.target.value)}/>
              <button type="button" className="sp-btn-primary" disabled={busy || !name.trim() || name.trim() === org.name}
                onClick={() => rename.mutate(name.trim())}>
                Переименовать
              </button>
            </div>
          )}
          {org.is_owner && !org.is_home && (
            <div>
              <button type="button" className="sp-btn-ghost org-danger" disabled={busy}
                onClick={() => {
                  if (window.confirm(`Удалить «${org.name}»? Участники перестанут видеть друг друга через неё.`)) del.mutate();
                }}>
                <Icon name="trash" size={12}/> Удалить организацию
              </button>
            </div>
          )}
          {!org.is_owner && !org.implicit && (
            <div>
              <button type="button" className="sp-btn-ghost org-danger" disabled={busy}
                onClick={() => {
                  const ask = org.status === "pending"
                    ? `Отозвать заявку в «${org.name}»?`
                    : `Выйти из «${org.name}»? Люди оттуда перестанут видеть вас в списках.`;
                  if (window.confirm(ask)) leave.mutate();
                }}>
                {org.status === "pending" ? "Отозвать заявку" : "Выйти из организации"}
              </button>
            </div>
          )}
          {!org.is_owner && org.implicit && (
            <div className="org-hint">Сотрудники состоят в «{org.name}» всегда — выйти самому нельзя.</div>
          )}
        </div>
      </div>

      {err && <div className="sp-error">{err}</div>}
    </div>
  );
}


/** Владелец или администратор сам добавляет человека (без заявки с его стороны). */
function OrgAdder({ org, onAdded }: { org: Org; onAdded: (d: OrgMembers) => void }) {
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [note, setNote] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setDq(q.trim()), 250);
    return () => window.clearTimeout(t);
  }, [q]);

  const candQ = useQuery({
    queryKey: ["orgs/candidates", org.id, dq],
    queryFn: () => fetchOrgCandidates(org.id, dq),
    enabled: dq.length >= 2,
    staleTime: 5_000,
  });
  const addMut = useMutation({
    mutationFn: async (c: OrgCandidate) => ({
      c, d: await addOrgMember(org.id, c.via === "login" ? { login: dq } : { user_id: c.user_id }),
    }),
    onSuccess: ({ c, d }) => {
      onAdded(d);
      setErr(null);
      setQ(""); setDq("");
      setNote(`${c.display_name || "Человек"} теперь в «${org.name}». Ему придёт уведомление.`);
    },
    onError: (e) => setErr(errText(e)),
  });
  const list = dq.length >= 2 ? (candQ.data ?? []) : [];

  return (
    <div>
      <div className="org-label">Добавить участника</div>
      <input
        className="sp-input"
        type="search"
        value={q}
        onChange={(e) => { setQ(e.target.value); setNote(null); setErr(null); }}
        placeholder="Имя, почта или логин"
        maxLength={120}
        autoComplete="off"
        aria-label="Кого добавить"
      />
      {dq.length >= 2 && (
        <div className="org-list org-results">
          {candQ.isLoading && <div className="org-empty">Ищем…</div>}
          {!candQ.isLoading && list.length === 0 && (
            <div className="org-empty">
              Никого не нашли. Если человека нет у вас в мессенджере, введите его почту или логин целиком.
            </div>
          )}
          {list.map(c => (
            <div key={c.user_id} className="org-row">
              <Avatar name={c.display_name || "?"} userId={c.user_id} avatarFilename={c.avatar_filename} size={32} color="#4fae4e"/>
              <div className="org-row-body">
                <div className="org-row-name">{c.display_name || "Без имени"}</div>
                {c.via === "login" && <div className="org-row-sub">найден по почте или логину</div>}
              </div>
              <div className="org-row-actions">
                <button type="button" className="sp-btn-primary org-btn-sm" disabled={addMut.isPending}
                  onClick={() => addMut.mutate(c)}>
                  Добавить
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {!note && !err && (
        <div className="org-hint">
          По имени — среди тех, кого вы видите в мессенджере; остальных — по точной почте или логину.
        </div>
      )}
      {note && <div className="org-note"><Icon name="check" size={14}/><span>{note}</span></div>}
      {err && <div className="sp-error">{err}</div>}
    </div>
  );
}


function OrgFinder({ onAction }: { onAction?: (message: string) => void }) {
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [note, setNote] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const inval = useInvalidateOrgs();

  useEffect(() => {
    const t = window.setTimeout(() => setDq(q.trim()), 250);
    return () => window.clearTimeout(t);
  }, [q]);

  const searchQ = useQuery({
    queryKey: ["orgs/search", dq],
    queryFn: () => searchOrgs(dq),
    enabled: dq.length >= 2,
    staleTime: 10_000,
  });

  const done = (message: string) => { setErr(null); setNote(message); onAction?.(message); inval(); };
  const joinMut = useMutation({
    mutationFn: async (o: Org) => ({ o, r: await joinOrg(o.id) }),
    onSuccess: ({ o, r }) => done(r.status === "member"
      ? `Вы в «${o.name}».`
      : `Заявка в «${o.name}» отправлена. Когда её подтвердят, вы и коллеги увидите друг друга.`),
    onError: (e) => setErr(errText(e)),
  });
  const createMut = useMutation({
    mutationFn: (name: string) => createOrg(name),
    onSuccess: (o) => {
      setQ(""); setDq("");
      done(`Организация «${o.name}» создана, вы её владелец. Коллеги найдут её по названию и отправят заявку — подтверждать будете вы или назначенные вами администраторы.`);
    },
    onError: (e) => setErr(errText(e)),
  });

  const busy = joinMut.isPending || createMut.isPending;
  const results = dq.length >= 2 ? (searchQ.data ?? []) : [];
  const exact = results.some(o => sameName(o.name, dq));

  return (
    <div>
      <div className="org-label">Найти или создать организацию</div>
      <input
        className="sp-input"
        type="search"
        value={q}
        onChange={(e) => { setQ(e.target.value); setNote(null); setErr(null); }}
        placeholder="Название организации"
        maxLength={120}
        autoComplete="off"
        aria-label="Название организации"
      />
      {dq.length >= 2 && (
        <div className="org-list org-results">
          {searchQ.isLoading && <div className="org-empty">Ищем…</div>}
          {!searchQ.isLoading && results.map(o => (
            <div key={o.id} className="org-row">
              <div className="org-mark"><Icon name="users" size={16}/></div>
              <div className="org-row-body">
                <div className="org-row-name">{o.name}</div>
                <div className="org-row-sub">
                  {membersText(o.members)}{o.owner_name ? ` · владелец ${o.owner_name}` : ""}
                </div>
              </div>
              <div className="org-row-actions">
                {o.status === "member" ? (
                  <span className="org-tag">Вы участник</span>
                ) : o.status === "pending" ? (
                  <span className="org-tag is-pending">Заявка отправлена</span>
                ) : (
                  <button type="button" className="sp-btn-primary org-btn-sm" disabled={busy}
                    onClick={() => joinMut.mutate(o)}>
                    Я работаю здесь
                  </button>
                )}
              </div>
            </div>
          ))}
          {!searchQ.isLoading && !exact && (
            <div className="org-row">
              <div className="org-mark is-new"><Icon name="plus" size={16}/></div>
              <div className="org-row-body">
                <div className="org-row-name">Создать «{dq}»</div>
                <div className="org-row-sub">Вы станете владельцем и будете подтверждать коллег</div>
              </div>
              <div className="org-row-actions">
                <button type="button" className="sp-btn-ghost org-btn-sm" disabled={busy}
                  onClick={() => createMut.mutate(dq)}>
                  Создать
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {note && <div className="org-note"><Icon name="check" size={14}/><span>{note}</span></div>}
      {err && <div className="sp-error">{err}</div>}
    </div>
  );
}


/** «Где вы работаете?» — гостю сразу после регистрации, пока нет ни организации, ни заявки. */
export function OrgsOnboarding() {
  const { user } = useAuth();
  const storageKey = user ? `kb:orgs-onboarding:${user.id}` : "";
  // «Пропустить» — до следующего открытия мессенджера, не навсегда (07.10.2026: гость пропустил окно, заявки не было,
  // окно больше не показывалось — его никто не видел и о нём никто не знал). Пока нет ни организации, ни заявки — спросим снова.
  const [dismissed, setDismissed] = useState(() => {
    try { return !!storageKey && sessionStorage.getItem(storageKey) === "1"; } catch { return false; }
  });
  const [done, setDone] = useState<string | null>(null);
  const myQ = useQuery({
    queryKey: ORGS_KEY,
    queryFn: fetchMyOrgs,
    enabled: !!user && user.role === "external" && !dismissed,
    staleTime: 15_000,
  });

  const close = () => {
    try { if (storageKey) sessionStorage.setItem(storageKey, "1"); } catch { /* приватный режим */ }
    setDismissed(true);
  };

  if (!user || user.role !== "external" || dismissed || !myQ.data) return null;
  if (myQ.data.orgs.length > 0 && !done) return null;

  return createPortal(
    <div className="self-profile-modal chat-redesign">
      <div className="self-profile-card org-onboard" role="dialog" aria-modal="true" aria-labelledby="org-onboard-title">
        <div className="self-profile-body">
          <div className="org-onboard-title" id="org-onboard-title">Где вы работаете?</div>
          <div className="org-intro">
            Найдите свою организацию и отправьте заявку. Когда её подтвердят,
            вы и коллеги увидите друг друга в мессенджере. Пока организации нет, вас никто не видит в списках.
          </div>
          <OrgFinder onAction={setDone}/>
          <div className="org-onboard-foot">
            <button type="button" className={done ? "sp-btn-primary" : "sp-btn-ghost"} onClick={close}>
              {done ? "Перейти в мессенджер" : "Пропустить"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
